if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      // Initialization in connectedCallback (not constructor) so that children are
      // guaranteed to be in the DOM — the constructor fires during innerHTML parsing
      // on a detached node (HTMLUpdateUtility.setInnerHTML), where querySelector returns null.
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;

        this.variantIdInput.disabled = false;

        // Reuse the same bound reference across reconnects to keep removeEventListener working.
        if (!this._onSubmit) this._onSubmit = this.onSubmitHandler.bind(this);
        this.form.addEventListener('submit', this._onSubmit);

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton?.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      disconnectedCallback() {
        if (this.form && this._onSubmit) {
          this.form.removeEventListener('submit', this._onSubmit);
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        console.count('[ATC] onSubmitHandler fired');

        // === CART REQUEST DEBUG ===
        const _dbgInput = this.form?.querySelector('input[name="id"]');
        console.log('=== CART REQUEST DEBUG ===', {
          designMode:           window.Shopify?.designMode,
          productFormConnected: this.isConnected,
          formConnected:        this.form?.isConnected,
          formId:               this.form?.id,
          formAction:           this.form?.getAttribute('action'),
          formVariantId:        _dbgInput?.value,
          formVariantDisabled:  _dbgInput?.disabled,
          submitBtnDisabled:    this.submitButton?.hasAttribute('disabled'),
          submitBtnAriaDisabled: this.submitButton?.getAttribute('aria-disabled'),
          sectionId:            this.closest('product-info')?.dataset?.section,
          timestamp:            Date.now(),
        });

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Log every FormData entry to catch missing or duplicated id fields
        const _dbgFormEntries = {};
        for (const [k, v] of formData.entries()) { _dbgFormEntries[k] = v; }
        console.log('ACTUAL CART PAYLOAD', _dbgFormEntries);

        // Guard: if the variant ID input is disabled, id is absent from FormData.
        const variantId = formData.get('id');
        if (!variantId) {
          console.error('[ATC] Varianten-ID fehlt – Abbruch', {
            inputDisabled: _dbgInput?.disabled,
            inputValue:    _dbgInput?.value,
          });
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner')?.classList.add('hidden');
          this.handleErrorMessage(window.variantStrings.unavailable);
          return;
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        console.log('[ATC] Cart-Request wird gesendet', {
          variantId:    formData.get('id'),
          quantity:     formData.get('quantity'),
          cartAddUrl:   routes.cart_add_url,
        });
        console.count('REAL CART REQUEST SENT');

        fetch(this.form.getAttribute('action') || `${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              // MarketPrint's Cart Validation Function rejects editor sessions server-side
              // (likely due to missing buyer identity in preview context). This cannot
              // be bypassed client-side — silently reset the button so the merchant
              // sees a working "In den Warenkorb" state while editing the theme.
              if (window.Shopify?.designMode) {
                console.log('[ATC Theme Editor] Cart-Validierung blockiert Editor-Vorschau (serverseitig) – Button wird zurückgesetzt');
                this.error = false;
                return;
              }

              console.error('SHOPIFY CART ERROR', {
                status:          response.status,
                message:         response.message,
                description:     response.description,
                errors:          response.errors,
                sentVariantId:   formData.get('id'),
                formVariantId:   this.form?.querySelector('[name="id"]')?.value,
                formConnected:   this.form?.isConnected,
              });

              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.error = true;

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              } else {
                this.toggleSubmitButton(true, window.variantStrings.soldOut);
              }
              return;
            } else if (!this.cart) {
              console.log('SHOPIFY CART RESPONSE', { status: 'ok', data: response });
              window.location = window.routes.cart_url;
              return;
            }

            console.log('SHOPIFY CART RESPONSE', { status: 'ok', itemCount: response.item_count });

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
